import { useState } from "react";
import { NavLink } from "react-router";
import {
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Activity,
  Settings2,
  Menu,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth/hooks";
import { can } from "@/lib/auth/roles";
import { TenantSwitcher } from "@/components/layout/TenantSwitcher";
import { UserMenu } from "@/components/layout/UserMenu";
import { Button } from "@/components/ui/button";

const NAV_ITEMS = [
  {
    to: "/",
    label: "Dashboard",
    permission: "reports.read" as const,
    exact: true,
    Icon: LayoutDashboard,
  },
  { to: "/reports", label: "Reports", permission: "reports.read" as const, Icon: FileText },
  {
    to: "/exceptions",
    label: "Exceptions",
    permission: "exceptions.read" as const,
    Icon: AlertTriangle,
  },
  { to: "/sync", label: "Sync", permission: "sync.read" as const, Icon: Activity },
  { to: "/admin", label: "Admin", permission: "admin.mapping.read" as const, Icon: Settings2 },
];

export function TopNav() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const items = NAV_ITEMS.filter((item) => can(user.role, item.permission));

  return (
    <nav
      role="navigation"
      aria-label="Primary"
      className="container flex h-16 items-center justify-between gap-4"
    >
      <div className="flex items-center gap-1 md:gap-6">
        <NavLink
          to="/"
          end
          className="flex items-center gap-2 text-sm font-semibold tracking-tight"
        >
          <span
            aria-hidden="true"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-soft-sm"
          >
            <span className="text-[13px] font-bold">L</span>
          </span>
          <span className="hidden sm:inline-flex flex-col leading-tight">
            <span className="text-sm font-semibold">Luxfair Reports</span>
            <span className="text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
              Cloudbeds → SFM
            </span>
          </span>
        </NavLink>

        {/* Desktop nav */}
        <ul className="hidden items-center gap-0.5 md:flex">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.exact}
                className={({ isActive }) =>
                  cn(
                    "nav-link inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200",
                    "hover:bg-muted hover:text-foreground",
                    isActive ? "text-foreground" : "text-muted-foreground",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.Icon className="h-4 w-4" aria-hidden="true" />
                    {item.label}
                    <span data-active={isActive} className="hidden" />
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center gap-2">
        <div className="hidden md:flex">
          <TenantSwitcher />
        </div>
        <UserMenu />
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="absolute inset-x-0 top-16 z-20 border-b border-border bg-background shadow-soft-md md:hidden">
          <ul className="container flex flex-col gap-1 py-3">
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.exact}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted",
                    )
                  }
                >
                  <item.Icon className="h-4 w-4" aria-hidden="true" />
                  {item.label}
                </NavLink>
              </li>
            ))}
            <li className="px-3 pt-2">
              <TenantSwitcher />
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}
